import { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Form as AntForm,
  Input,
  InputNumber,
  Switch,
  Radio,
  Transfer,
  Tag,
  Alert,
  Descriptions,
  Tooltip,
  Typography,
  message,
  Button,
} from 'antd';
import {
  InfoCircleOutlined,
  StopOutlined,
  CheckCircleOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { useForm, FormProvider, useWatch, Controller } from 'react-hook-form';
import type { SubmitHandler, SubmitErrorHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { authFetch } from '../../utils/api';
import { getBrandLabel, tokenManager, type Brand } from '../../utils/tokenManager';
import { FormField, FormSection, FormActions } from '../../shared/form';
import { targetBrandForUpsert } from '../../shared/form/writeTargetBrand';
import { TierUpsertSchema, type TierUpsertDTO } from '../../schemas/tier';
import { brandVisibility } from './brandVisibility';

export const TIER_SNAPSHOT_POLICY_NOTICE = '一般 Tier 設定變更只影響之後發布的付費權益快照，已發布的付費權益保留購買時設定。例外：明確將節點或協議設為空陣列 []，會立即封鎖此 Tier 的所有既有快照。PROMO、v2 與舊版權益仍依目前 Tier 設定運作。';

export interface TierRecordForForm {
  id: number;
  /**
   * 去品牌化 M4：這筆等級 row 的實際品牌。編輯（PUT /:id）= 既有 row 寫入，
   * 目標品牌 = 該 row 的品牌（列表已知，`TierRecord.brand`），顯式帶
   * X-Target-Brand，絕不繼承檢視範圍 / activeBrand。新增流程無 row → 用
   * effectiveBrand（單一品牌檢視）。
   */
  brand?: Brand;
  name: string;
  description: string | null;
  level: number;
  maxDevices: number;
  downloadLimitMbps: number;
  uploadLimitMbps: number;
  dailyTrafficMb: number;
  sessionDurationMinutes: number;
  sortOrder: number;
  isFree: boolean;
  canAccessVless: boolean;
  /** Sprint 21 / W6-B — DAITA UNION ACL opt-in (default false). */
  canAccessDaita?: boolean;
  priceMonthly: number | null;
  priceYearly: number | null;
  allowedNodeIds?: number[] | null;
}

interface NodeOption {
  id: number;
  name: string;
  region: string | null;
  ip: string;
  /**
   * 節點 brand 可見性（§5.3 三態徽章）。fail-OPEN：NULL / 空陣列 = 對全部品牌
   * 共享可見（共享基礎設施）。**保留**此欄位（先前 setNodeOptions map 丟棄它）才能
   * 在穿梭框逐節點標示品牌可見性。
   */
  allowedBrands: Brand[] | null;
}

/**
 * 單節點三態品牌徽章（P1 唯讀標示）。
 * ✅ 本品牌可見 / 🌐 全品牌共享 / ⛔ 僅其他品牌。純顯示，不觸發任何寫入。
 */
const BrandVisibilityBadge: React.FC<{
  allowedBrands: Brand[] | null | undefined;
  activeBrand: Brand;
}> = ({ allowedBrands, activeBrand }) => {
  const vis = brandVisibility(allowedBrands, activeBrand);
  if (vis === 'shared') {
    return (
      <Tooltip title="未設定品牌限制（allowedBrands 為空）＝共享基礎設施，對全部品牌共享可見。本品牌唯讀可見、非隱藏。">
        <Tag color="blue" icon={<GlobalOutlined />} style={{ margin: 0 }}>
          全品牌共享
        </Tag>
      </Tooltip>
    );
  }
  if (vis === 'allowed') {
    return (
      <Tag color="green" icon={<CheckCircleOutlined />} style={{ margin: 0 }}>
        本品牌可見
      </Tag>
    );
  }
  const labels = (allowedBrands ?? []).map((b) => getBrandLabel(b)).join('、');
  return (
    <Tooltip
      title={`此節點僅對「${labels}」可見，不含目前品牌 → 本品牌用戶不會收到此節點（即使加入白名單也不下發）。`}
    >
      <Tag color="red" icon={<StopOutlined />} style={{ margin: 0 }}>
        僅其他品牌
      </Tag>
    </Tooltip>
  );
};

interface Props {
  open: boolean;
  /** null = 新增、TierRecordForForm = 編輯 */
  editing: TierRecordForForm | null;
  /**
   * 去品牌化 M1/M4：目前檢視範圍對應的單一品牌（聚合檢視 = null）。
   * 新增等級時作為顯式目標品牌；null 由父層 gate 擋住不會進到這裡。
   */
  effectiveBrand: Brand | null;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * W3-G (sprint19, Sprint 21 F3 lite) — Effective ACL summary returned by
 * `GET /api/admin/tiers/:id/effective-acl`. Read-only computed.
 */
interface EffectiveAclSummary {
  tierId: number;
  name: string;
  userCount: number;
  sk6NodeCount: number;
  vlessNodeCount: number;
  canAccessVless: boolean;
  allowedProtocols: string[] | null;
  denyAll: boolean;
  allowAll: boolean;
  explicitAllowlist: boolean;
}

const DEFAULTS: TierUpsertDTO = {
  name: '',
  description: '',
  level: 0,
  maxDevices: 1,
  downloadLimitMbps: 0,
  uploadLimitMbps: 0,
  dailyTrafficMb: 0,
  sessionDurationMinutes: 0,
  sortOrder: 0,
  isFree: true,
  canAccessVless: false,
  // Sprint 21 / W6-B — DAITA UNION ACL opt-in (default false).
  canAccessDaita: false,
  priceMonthly: null,
  priceYearly: null,
  allowedNodesMode: 'all',
  allowedNodeIds: [],
};

/**
 * 等級新增/編輯表單 — Pattern A++（單一 Modal 內含多個 FormSection 分區）
 *
 * 為什麼不是純 Pattern B（全頁）：
 * - 欄位 11 個但邏輯密度低，分區後 Modal 仍可容納
 * - 等級 CRUD 屬於高頻運營動作，drawer/modal 比 full page 更快
 * - 真正的 Pattern B 範本會留給 nodes（含 JSON Monaco editor）
 */
export const TierFormModal: React.FC<Props> = ({
  open,
  editing,
  effectiveBrand,
  onClose,
  onSaved,
}) => {
  // 品牌視角：優先用檢視範圍的單一品牌；聚合檢視（null，僅編輯情境）退回 JWT 值做 ACL 顯示。
  // 新增流程已由父層 gate 保證此時 effectiveBrand 非 null。
  const activeBrand = effectiveBrand ?? tokenManager.getActiveBrand();
  const activeBrandLabel = getBrandLabel(activeBrand);
  const isKshield = activeBrand === 'kshield';
  const form = useForm<TierUpsertDTO>({
    resolver: zodResolver(TierUpsertSchema),
    mode: 'onBlur',
    defaultValues: DEFAULTS,
  });

  // 節點列表（供 Transfer 使用）
  const [nodeOptions, setNodeOptions] = useState<NodeOption[]>([]);
  const [nodesLoading, setNodesLoading] = useState(false);
  // 後端寫入拒絕訊息（持久顯示，不像 message.error 幾秒就消失、操作者常錯過）。
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setNodesLoading(true);
      try {
        const res = await authFetch('/api/admin/nodes?page=1&pageSize=100');
        const result = await res.json();
        if (cancelled) return;
        if (result.code === 0) {
          const list = Array.isArray(result.data?.list)
            ? result.data.list
            : Array.isArray(result.data)
              ? result.data
              : [];
          setNodeOptions(
            list.map((n: Record<string, unknown>) => ({
              id: Number(n.id),
              name: String(n.name ?? ''),
              region: (n.region as string) ?? null,
              ip: String(n.ip ?? ''),
              // 保留品牌可見性（先前被 map 丟棄）。fail-OPEN：非陣列 → null = 全品牌共享。
              allowedBrands: Array.isArray(n.allowedBrands)
                ? (n.allowedBrands as Brand[])
                : null,
            }))
          );
        }
      } catch {
        // ignore
      } finally {
        if (!cancelled) setNodesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // 開啟時注入既有值或重設
  useEffect(() => {
    if (open) {
      if (editing) {
        // W3-G tri-state derivation:
        // - allowedNodeIds=null/undefined → 'all'
        // - allowedNodeIds=[] (empty)     → 'deny-all'  (explicit lockdown)
        // - allowedNodeIds=[1,2,…]        → 'select'
        const allowedIds = editing.allowedNodeIds;
        let mode: 'all' | 'select' | 'deny-all' = 'all';
        if (Array.isArray(allowedIds)) {
          mode = allowedIds.length === 0 ? 'deny-all' : 'select';
        }
        form.reset({
          name: editing.name,
          description: editing.description ?? '',
          level: editing.level,
          maxDevices: editing.maxDevices,
          downloadLimitMbps: editing.downloadLimitMbps ?? 0,
          uploadLimitMbps: editing.uploadLimitMbps ?? 0,
          dailyTrafficMb: editing.dailyTrafficMb ?? 0,
          sessionDurationMinutes: editing.sessionDurationMinutes ?? 0,
          sortOrder: editing.sortOrder,
          isFree: editing.isFree ?? true,
          canAccessVless: editing.canAccessVless ?? false,
          // Sprint 21 / W6-B — DAITA UNION ACL opt-in (default false).
          canAccessDaita: editing.canAccessDaita ?? false,
          priceMonthly: editing.priceMonthly,
          priceYearly: editing.priceYearly,
          allowedNodesMode: mode,
          allowedNodeIds:
            mode === 'select' ? [...(allowedIds as number[])] : [],
        });
      } else {
        form.reset(DEFAULTS);
      }
    }
  }, [open, editing, form]);

  /* ── Effective ACL preview (W3-G / Sprint 21 F3 lite) ── */
  const [effectiveAcl, setEffectiveAcl] =
    useState<EffectiveAclSummary | null>(null);
  const [aclLoading, setAclLoading] = useState(false);

  useEffect(() => {
    if (!open || !editing) {
      setEffectiveAcl(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setAclLoading(true);
      try {
        const res = await authFetch(
          `/api/admin/tiers/${editing.id}/effective-acl`
        );
        const result = await res.json();
        if (cancelled) return;
        if (result.code === 0 && result.data) {
          setEffectiveAcl(result.data as EffectiveAclSummary);
        }
      } catch {
        // ignore — preview is best-effort
      } finally {
        if (!cancelled) setAclLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, editing]);

  // 條件欄位：付費才顯示定價
  const isFree = useWatch({ control: form.control, name: 'isFree' });
  const allowedNodesMode = useWatch({
    control: form.control,
    name: 'allowedNodesMode',
  });
  const canAccessVlessLive = useWatch({
    control: form.control,
    name: 'canAccessVless',
  });
  const selectedNodeIdsLive =
    useWatch({ control: form.control, name: 'allowedNodeIds' }) ?? [];

  /**
   * 孤兒節點 = 已選 (allowedNodeIds) 但不在本品牌可見節點清單 (nodeOptions) 內的 ID。
   * 成因：歷史資料把其他品牌專用節點塞進本 tier（例如用 all-brand admin 繞過 brand
   * 過濾寫入），或節點 allowedBrands 後來被改窄。這些 ID 在 Transfer 左右兩側都不會
   * 出現（Transfer 只 render dataSource 內的 item）→ UI 無法移除；但提交時後端
   * normalizeAllowedNodeIds 對它們 throw「以下節點不存在或不可見」→ 整筆儲存失敗，
   * 操作者陷入「看到有未儲存變更卻怎麼都存不了、也找不到問題節點」的死局。
   * 在 nodeOptions 載入完成前不判定（否則全部會被誤當孤兒）。
   */
  const orphanNodeIds = useMemo(() => {
    if (nodesLoading || nodeOptions.length === 0) return [];
    const known = new Set(nodeOptions.map((n) => n.id));
    return (selectedNodeIdsLive ?? []).filter((id) => !known.has(id));
  }, [nodesLoading, nodeOptions, selectedNodeIdsLive]);

  const removeOrphanNodes = () => {
    if (orphanNodeIds.length === 0) return;
    const orphanSet = new Set(orphanNodeIds);
    const cleaned = (selectedNodeIdsLive ?? []).filter(
      (id) => !orphanSet.has(id)
    );
    form.setValue('allowedNodeIds', cleaned, { shouldDirty: true });
  };

  /**
   * 跨品牌節點 = 在本品牌節點清單裡、但 allowedBrands 非空且不含目前品牌（other-only）。
   * 與孤兒節點（根本不在 nodeOptions）不同：這些節點看得到，但對本品牌用戶不可用。
   * 用 isBrandAllowed 語意判定（NULL/空＝全品牌共享，不算 other-only）。P1 唯讀，只警示不改寫入。
   */
  const selectedCrossBrandIds = useMemo(() => {
    if (nodesLoading || nodeOptions.length === 0) return [];
    const crossBrand = new Set(
      nodeOptions
        .filter((n) => brandVisibility(n.allowedBrands, activeBrand) === 'other-only')
        .map((n) => n.id)
    );
    return (selectedNodeIdsLive ?? []).filter((id) => crossBrand.has(id));
  }, [nodesLoading, nodeOptions, selectedNodeIdsLive, activeBrand]);

  /* ── Node-type totals for Effective ACL preview ── */
  const totalSk6Nodes = useMemo(
    () =>
      nodeOptions.filter(n => {
        // best-effort detect SK6 nodes; without explicit nodeType in the
        // GET /api/admin/nodes endpoint we treat everything not flagged
        // as VLESS as SK6. The preview is illustrative — server is the
        // single source of truth via /effective-acl.
        const rawType = (n as { nodeType?: string }).nodeType;
        return rawType !== 'vless';
      }).length,
    [nodeOptions]
  );
  const totalVlessNodes = useMemo(
    () =>
      nodeOptions.filter(
        n => (n as { nodeType?: string }).nodeType === 'vless'
      ).length,
    [nodeOptions]
  );

  /**
   * W3-G submit dispatcher. For deny-all, ask the operator to confirm via
   * `Modal.confirm` before firing the PUT — the server also enforces
   * `confirmDenyAll: true` server-side, so this UI gate is a usability
   * layer, not the only line of defense.
   */
  const submitWithMode = async (
    values: TierUpsertDTO,
    confirmDenyAll: boolean
  ) => {
    const { allowedNodesMode: mode, allowedNodeIds, ...rest } = values;
    let canonicalAllowedNodeIds: number[] | null;
    if (mode === 'all') canonicalAllowedNodeIds = null;
    else if (mode === 'deny-all') canonicalAllowedNodeIds = [];
    else
      canonicalAllowedNodeIds = Array.isArray(allowedNodeIds)
        ? allowedNodeIds
        : [];

    const payload: Record<string, unknown> = {
      ...rest,
      description: rest.description || undefined,
      priceMonthly: isKshield ? null : rest.priceMonthly,
      priceYearly: isKshield ? null : rest.priceYearly,
      allowedNodeIds: canonicalAllowedNodeIds,
    };
    if (mode === 'deny-all') {
      payload.confirmDenyAll = confirmDenyAll;
    }

    const url = editing ? `/api/admin/tiers/${editing.id}` : '/api/admin/tiers';
    const method = editing ? 'PUT' : 'POST';
    // M4：寫入一律顯式帶目標品牌，絕不繼承檢視範圍 / activeBrand。
    // - 新增：目標品牌 = 目前單一品牌檢視（effectiveBrand，父層 gate 已保證非 null）。
    // - 編輯（既有 row）：目標品牌 = 該 tier row 的實際品牌（editing.brand，列表已帶
    //   brand 欄），對齊 backend resolveExistingRowWriteTargetBrand（enforce ON 下缺顯式
    //   → 400；顯式與 row 不符 → 403）。
    const t = targetBrandForUpsert(!!editing, editing?.brand, effectiveBrand);
    if (!t.ok) {
      message.error(t.reason);
      setSubmitError(t.reason);
      return;
    }
    payload.targetBrand = t.brand;
    const headers: Record<string, string> = { 'X-Target-Brand': t.brand };
    const res = await authFetch(url, {
      method,
      headers,
      body: JSON.stringify(payload),
    });
    const result = await res.json();
    if (result.code === 0) {
      setSubmitError(null);
      message.success(editing ? '等級已更新' : '等級新增成功');
      onSaved();
      onClose();
    } else {
      const msg = result.msg || '操作失敗';
      message.error(msg);
      // 持久顯示後端拒絕原因（toast 幾秒就消失，操作者常錯過 → 誤以為只是「有未儲存變更」）。
      setSubmitError(msg);
      if (result.msg?.includes('已存在')) {
        form.setError('name', { message: result.msg });
      }
    }
  };

  const onSubmit: SubmitHandler<TierUpsertDTO> = async (values) => {
    setSubmitError(null);
    // W3-G: deny-all path triggers a confirmation modal before send.
    if (values.allowedNodesMode === 'deny-all') {
      Modal.confirm({
        title: '確認將等級設為 deny-all？',
        icon: <StopOutlined style={{ color: '#cf1322' }} />,
        content: (
          <div>
            <p style={{ marginTop: 0 }}>
              <strong>{values.name || editing?.name}</strong>{' '}
              將被設為 <Tag color="red">DENY-ALL</Tag>。
            </p>
            <p>
              此等級的所有用戶會{' '}
              <strong>失去所有 SK6 節點存取權</strong>
              （海外加速 VLESS 仍受 「可存取海外加速」 開關控制）。
            </p>
            <p style={{ marginBottom: 0 }}>
              用戶數（目前）：
              <strong>{effectiveAcl?.userCount ?? '—'}</strong>
            </p>
          </div>
        ),
        okText: '確認設為 deny-all',
        cancelText: '取消',
        okButtonProps: { danger: true },
        onOk: async () => {
          await submitWithMode(values, true);
        },
      });
      return;
    }
    await submitWithMode(values, false);
  };

  // 修 #3：handleSubmit 先前無 onInvalid → zod 驗證失敗時 onSubmit 根本不被呼叫且零提示
  //（ACL 欄位用裸 Controller zod 錯誤不顯示、name 51-64 字過前端但後端 422 等情形 user
  // 都看不到原因 → 誤以為「按了沒反應、存不進去」）。補 onInvalid：遞迴撈第一個錯誤欄位
  // message，持久顯示（submitError）+ toast，讓 user 知道哪個欄位擋住儲存。
  const onInvalid: SubmitErrorHandler<TierUpsertDTO> = (errors) => {
    const firstMessage = (node: unknown): string | null => {
      if (!node || typeof node !== 'object') return null;
      const maybe = node as { message?: unknown };
      if (typeof maybe.message === 'string' && maybe.message) return maybe.message;
      for (const v of Object.values(node as Record<string, unknown>)) {
        const m = firstMessage(v);
        if (m) return m;
      }
      return null;
    };
    const msg =
      firstMessage(errors) || '表單有欄位未通過驗證，請檢查標紅的欄位後再儲存';
    setSubmitError(msg);
    message.error(msg);
  };

  return (
    <Modal
      title={
        editing
          ? `編輯 ${activeBrandLabel} 等級：${editing.name}`
          : `新增 ${activeBrandLabel} 等級`
      }
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnHidden
      width={allowedNodesMode === 'select' ? 900 : 600}
    >
      <Alert type="warning" showIcon message="Tier 修改的影響範圍" description={TIER_SNAPSHOT_POLICY_NOTICE} style={{ marginBottom: 16 }} />
      <FormProvider {...form}>
        <AntForm layout="vertical" onFinish={form.handleSubmit(onSubmit, onInvalid)}>
          <FormSection title="基本資訊" description="等級識別與排序">
            <FormField<TierUpsertDTO>
              name="name"
              label="名稱"
              required
              control={
                <Input
                  placeholder={
                    isKshield ? '例如：內部測試、合作夥伴' : '例如：免費版、標準版'
                  }
                />
              }
            />
            <FormField<TierUpsertDTO>
              name="description"
              label="描述"
              control={<Input.TextArea placeholder="等級說明（選填）" rows={2} />}
            />
            <FormField<TierUpsertDTO>
              name="level"
              label="等級"
              required
              help="數值越大等級越高，用於 schedule 比較"
              control={<InputNumber min={0} max={999} style={{ width: '100%' }} />}
            />
            <FormField<TierUpsertDTO>
              name="sortOrder"
              label="顯示排序"
              required
              help="數值越小越靠前"
              control={<InputNumber min={0} style={{ width: '100%' }} />}
            />
          </FormSection>

          <FormSection title="配額限制" description="設為 0 代表無限制">
            <FormField<TierUpsertDTO>
              name="maxDevices"
              label="最大裝置數"
              required
              help="允許同時連線的裝置數，1-100"
              control={<InputNumber min={1} max={100} style={{ width: '100%' }} />}
            />
            <FormField<TierUpsertDTO>
              name="downloadLimitMbps"
              label="下載速度上限 (Mbps)"
              help="0 代表不限制下載速度"
              control={<InputNumber min={0} style={{ width: '100%' }} placeholder="0 = 無限制" />}
            />
            <FormField<TierUpsertDTO>
              name="uploadLimitMbps"
              label="上傳速度上限 (Mbps)"
              help="0 代表不限制上傳速度"
              control={<InputNumber min={0} style={{ width: '100%' }} placeholder="0 = 無限制" />}
            />
            <FormField<TierUpsertDTO>
              name="dailyTrafficMb"
              label="每日流量上限 (MB)"
              help="0 代表不限制每日流量"
              control={<InputNumber min={0} style={{ width: '100%' }} placeholder="0 = 無限制" />}
            />
            <FormField<TierUpsertDTO>
              name="sessionDurationMinutes"
              label="單次連線時長 (分鐘)"
              help="0 代表不限制單次連線時長"
              control={<InputNumber min={0} style={{ width: '100%' }} placeholder="0 = 無限制" />}
            />
          </FormSection>

          <FormSection title="存取控制">
            <FormField<TierUpsertDTO>
              name="canAccessVless"
              label="可存取海外加速 (VLESS)"
              help="開啟後此等級的用戶會看到 Cloudflare VLESS 節點"
              valuePropName="checked"
              control={<Switch checkedChildren="開放" unCheckedChildren="關閉" />}
            />
            <FormField<TierUpsertDTO>
              name="canAccessDaita"
              label="可存取 DAITA 流量混淆節點"
              help={
                'DAITA UNION 語意：此等級的成員可看到所有 DAITA-enabled 節點，' +
                '即使這些節點未列在「可用節點」白名單中（與海外加速 UNION 相同）。' +
                '節點 DAITA 開關由 vpn_config_json.daita.enabled 控制（W6-A 編輯）。'
              }
              valuePropName="checked"
              control={<Switch checkedChildren="開放" unCheckedChildren="關閉" />}
            />
          </FormSection>

          <FormSection
            title="可用節點"
            description={
              <span>
                選擇此等級的用戶能看到哪些節點。預設全部節點，含未來新建節點。
                <Tooltip
                  title={
                    <div style={{ maxWidth: 360 }}>
                      <strong>節點 ACL 優先序（E2#2 / Sprint 21）</strong>
                      <ol style={{ paddingLeft: 18, marginTop: 6 }}>
                        <li>節點 brand 可見性</li>
                        <li>節點 minTier 門檻</li>
                        <li>等級 allowedNodeIds（本欄位）</li>
                        <li>VLESS UNION：tier.canAccessVless=true 時 VLESS
                          節點繞過上述 sk6 white-list</li>
                      </ol>
                      <div style={{ marginTop: 4, opacity: 0.85 }}>
                        詳見後台 docs/tier-acl-precedence.md
                      </div>
                    </div>
                  }
                >
                  <InfoCircleOutlined
                    style={{ marginLeft: 6, color: '#1677ff' }}
                  />
                </Tooltip>
              </span>
            }
          >
            <AntForm.Item label="模式">
              <Controller
                control={form.control}
                name="allowedNodesMode"
                render={({ field }) => (
                  <Radio.Group
                    value={field.value}
                    onChange={(e) => {
                      const next = e.target.value as
                        | 'all'
                        | 'select'
                        | 'deny-all';
                      field.onChange(next);
                      if (next !== 'select') {
                        form.setValue('allowedNodeIds', [], {
                          shouldDirty: true,
                        });
                      }
                    }}
                  >
                    <Radio value="all" data-testid="tier-acl-mode-all">
                      <CheckCircleOutlined
                        style={{ color: '#52c41a', marginRight: 4 }}
                      />
                      全部節點（含未來新節點）
                    </Radio>
                    <Radio value="select" data-testid="tier-acl-mode-select">
                      指定節點（顯式白名單）
                    </Radio>
                    <Radio value="deny-all" data-testid="tier-acl-mode-deny">
                      <StopOutlined
                        style={{ color: '#cf1322', marginRight: 4 }}
                      />
                      <span style={{ color: '#cf1322' }}>
                        Deny-all（封鎖此等級）
                      </span>
                    </Radio>
                  </Radio.Group>
                )}
              />
            </AntForm.Item>
            {allowedNodesMode === 'deny-all' && (
              <Alert
                type="error"
                showIcon
                icon={<StopOutlined />}
                message="此等級會被設為 deny-all"
                description={
                  <div>
                    所有此等級用戶會 <strong>失去所有 SK6 節點存取權</strong>。
                    海外加速 (VLESS) 仍由 「可存取海外加速」 開關控制
                    （Phase 18 UNION semantic）。儲存時需於確認 modal
                    再次確認 (confirmDenyAll)。
                  </div>
                }
                style={{ marginBottom: 12 }}
                data-testid="tier-acl-deny-banner"
              />
            )}
            {allowedNodesMode === 'select' && orphanNodeIds.length > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                data-testid="tier-acl-orphan-warning"
                message="有不屬於本品牌的節點，會擋住儲存"
                description={
                  <div>
                    <p style={{ marginTop: 0 }}>
                      此等級的可用節點清單含 <strong>{orphanNodeIds.length}</strong>{' '}
                      個本品牌看不到、也連不到的節點（ID：
                      <code>{orphanNodeIds.join(', ')}</code>）。
                      這些節點不會出現在下方選擇器裡，但儲存時後端會以「節點不存在或不可見」
                      擋下<strong>整筆</strong>變更，導致無法儲存。請先移除它們再儲存。
                    </p>
                    <Button
                      danger
                      size="small"
                      onClick={removeOrphanNodes}
                      data-testid="tier-acl-remove-orphans"
                    >
                      移除 {orphanNodeIds.length} 個不可用節點
                    </Button>
                  </div>
                }
              />
            )}
            {allowedNodesMode === 'select' && selectedCrossBrandIds.length > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                data-testid="tier-acl-cross-brand-warning"
                message="已選清單含本品牌不可用節點"
                description={
                  <span>
                    已選的節點中有 <strong>{selectedCrossBrandIds.length}</strong>{' '}
                    個僅對其他品牌可見（allowedBrands 不含目前品牌{' '}
                    <strong>{activeBrandLabel}</strong>，標示為「⛔ 僅其他品牌」），
                    本品牌用戶不會收到這些節點（fail-OPEN 語意：只有 allowedBrands
                    為空才是全品牌共享）。節點 ID：
                    <code>{selectedCrossBrandIds.join(', ')}</code>。
                    此為唯讀提示、不影響儲存。
                  </span>
                }
              />
            )}
            {allowedNodesMode === 'select' && (
              <AntForm.Item label="選擇節點" required>
                <Controller
                  control={form.control}
                  name="allowedNodeIds"
                  render={({ field }) => {
                    const dataSource = nodeOptions.map((n) => ({
                      key: String(n.id),
                      title: n.name || n.ip,
                      region: n.region,
                      ip: n.ip,
                      allowedBrands: n.allowedBrands,
                    }));
                    const targetKeys = (field.value ?? []).map((id) => String(id));
                    return (
                      <Transfer
                        dataSource={dataSource}
                        targetKeys={targetKeys}
                        onChange={(next) => {
                          field.onChange(next.map((k) => Number(k)));
                        }}
                        disabled={nodesLoading}
                        showSearch
                        listStyle={{ width: 380, height: 320 }}
                        titles={['全部節點', '已選節點']}
                        render={(item: {
                          key: string;
                          title: string;
                          region: string | null;
                          ip: string;
                          allowedBrands: Brand[] | null;
                        }) => ({
                          label: (
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                                lineHeight: 1.35,
                                paddingTop: 2,
                                paddingBottom: 2,
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <span style={{ fontWeight: 500 }}>
                                  {item.title}
                                </span>
                                {item.region ? (
                                  <Tag style={{ margin: 0 }}>{item.region}</Tag>
                                ) : null}
                                {/* §5.3 三態品牌徽章（P1 唯讀標示） */}
                                <BrandVisibilityBadge
                                  allowedBrands={item.allowedBrands}
                                  activeBrand={activeBrand}
                                />
                              </div>
                              <span
                                style={{
                                  color: '#888',
                                  fontSize: 12,
                                  fontFamily:
                                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                  wordBreak: 'break-all',
                                }}
                              >
                                {item.ip}
                              </span>
                            </div>
                          ),
                          value: `${item.title} ${item.region ?? ''} ${item.ip}`,
                        })}
                      />
                    );
                  }}
                />
              </AntForm.Item>
            )}

            {/* W3-G / Sprint 21 F3 lite — Effective ACL live preview */}
            <EffectiveAclPreview
              editing={editing}
              loading={aclLoading}
              snapshot={effectiveAcl}
              allowedNodesMode={allowedNodesMode}
              selectedNodeIds={selectedNodeIdsLive}
              canAccessVless={!!canAccessVlessLive}
              totalSk6Nodes={totalSk6Nodes}
              totalVlessNodes={totalVlessNodes}
            />
          </FormSection>

          <FormSection
            title={isKshield ? '方案屬性' : '定價設定'}
            description={
              isKshield
                ? 'K盾不開放註冊、匿名試用或付費入口；此設定只用於內部權限分層。'
                : '僅供顯示，實際帳務由其他系統處理'
            }
          >
            <FormField<TierUpsertDTO>
              name="isFree"
              label="方案類型"
              valuePropName="checked"
              control={
                <Switch
                  checkedChildren={isKshield ? '預設方案' : '免費方案'}
                  unCheckedChildren={isKshield ? '內部方案' : '付費方案'}
                />
              }
            />
            {!isFree && !isKshield && (
              <>
                <FormField<TierUpsertDTO>
                  name="priceMonthly"
                  label="月費 (USD)"
                  control={
                    <InputNumber
                      min={0}
                      step={0.01}
                      style={{ width: '100%' }}
                      placeholder="例如 9.99"
                    />
                  }
                />
                <FormField<TierUpsertDTO>
                  name="priceYearly"
                  label="年費 (USD)"
                  control={
                    <InputNumber
                      min={0}
                      step={0.01}
                      style={{ width: '100%' }}
                      placeholder="例如 99.99"
                    />
                  }
                />
              </>
            )}
          </FormSection>

          {submitError && (
            <Alert
              type="error"
              showIcon
              closable
              onClose={() => setSubmitError(null)}
              style={{ marginTop: 16 }}
              data-testid="tier-form-submit-error"
              message="儲存失敗"
              description={submitError}
            />
          )}
          <FormActions
            onCancel={onClose}
            submitting={form.formState.isSubmitting}
            dirty={form.formState.isDirty}
            submitLabel={editing ? '儲存變更' : '建立等級'}
          />
        </AntForm>
      </FormProvider>
    </Modal>
  );
};

/* ───────── Effective ACL preview subcomponent (W3-G) ───────── */

interface EffectiveAclPreviewProps {
  editing: TierRecordForForm | null;
  loading: boolean;
  /** Latest server-computed snapshot for the tier being edited. */
  snapshot: EffectiveAclSummary | null;
  /** Live form state — drives the "after save" projection. */
  allowedNodesMode: 'all' | 'select' | 'deny-all';
  selectedNodeIds: number[];
  canAccessVless: boolean;
  /** Total reachable counts from the loaded node list (best-effort). */
  totalSk6Nodes: number;
  totalVlessNodes: number;
}

const EffectiveAclPreview: React.FC<EffectiveAclPreviewProps> = ({
  editing,
  loading,
  snapshot,
  allowedNodesMode,
  selectedNodeIds,
  canAccessVless,
  totalSk6Nodes,
  totalVlessNodes,
}) => {
  // Project the post-save counts from form state. Snapshot drives userCount
  // (since `users in this tier` doesn't change with the form mutation).
  const projectedSk6Count = (() => {
    if (allowedNodesMode === 'all') return totalSk6Nodes;
    if (allowedNodesMode === 'deny-all') return 0;
    return selectedNodeIds.length;
  })();
  const projectedVlessCount = canAccessVless ? totalVlessNodes : 0;
  const userCount = snapshot?.userCount ?? (editing ? 0 : 0);

  return (
    <div
      style={{
        marginTop: 12,
        padding: 12,
        background: '#fafafa',
        border: '1px solid #f0f0f0',
        borderRadius: 6,
      }}
      data-testid="tier-effective-acl-preview"
    >
      <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
        Effective ACL 即時預覽
        <Tooltip title="儲存後此等級用戶實際能存取的範圍。Server 端以 /effective-acl 為單一真相。">
          <InfoCircleOutlined
            style={{ marginLeft: 6, color: '#1677ff', fontSize: 12 }}
          />
        </Tooltip>
      </Typography.Text>
      <Descriptions
        size="small"
        column={2}
        styles={{ label: { width: 130 } }}
      >
        <Descriptions.Item label="此等級用戶數">
          {loading && !snapshot ? '—' : userCount}
        </Descriptions.Item>
        <Descriptions.Item label="可存取海外加速">
          {canAccessVless ? (
            <Tag color="purple">是</Tag>
          ) : (
            <Tag>否</Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="可達 SK6 節點">
          {allowedNodesMode === 'deny-all' ? (
            <Tag color="red">0 / {totalSk6Nodes}（DENY-ALL）</Tag>
          ) : allowedNodesMode === 'all' ? (
            <Tag color="green">
              {totalSk6Nodes} / {totalSk6Nodes}（全部）
            </Tag>
          ) : (
            <Tag color="blue">
              {projectedSk6Count} / {totalSk6Nodes}（白名單）
            </Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="可達 VLESS 節點">
          {projectedVlessCount === 0 ? (
            <Tag>0 / {totalVlessNodes}</Tag>
          ) : (
            <Tag color="purple">
              {projectedVlessCount} / {totalVlessNodes}（UNION）
            </Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="模式" span={2}>
          {allowedNodesMode === 'deny-all' ? (
            <Tag color="red" data-testid="tier-effective-acl-badge-deny">
              DENY-ALL
            </Tag>
          ) : allowedNodesMode === 'all' ? (
            <Tag color="green">ALLOW-ALL</Tag>
          ) : (
            <Tag color="blue">EXPLICIT-ALLOWLIST</Tag>
          )}
        </Descriptions.Item>
      </Descriptions>
    </div>
  );
};
